/**
 * Kamerakonfigurasjon.
 *
 * Tre kamerastater:
 *   1. overview     – Bærums Verk + Oslo i samme utsnitt, moderat pitch
 *   2. baerumsVerk  – tett på busskur-modellen (rutestart), høy pitch
 *   3. akerBrygge   – tett på klokketårn-modellen, høy pitch
 *
 * Ingen automatisk rutefølging ennå – kun manuell veksling.
 */

/**
 * Kamerastater brukt med map.flyTo().
 * Alle statene ser skrått FRA SØRØST (bearing -45 = kameraet står
 * i sørøst og ser mot nordvest). Ruten går vest→øst og leses da
 * diagonalt godt i alle utsnitt.
 */
export const CAMERA_STATES = {
  overview: {
    center: [10.66, 59.912], // mellom Bærum og Oslo
    zoom: 11.4, // rommer hele rutene Bærums Verk → Vika
    pitch: 52, // skrått nok til å lese 3D og hele ruten
    bearing: -45, // fra sørøst
    duration: 1600,
  },
  baerumsVerk: {
    center: [10.506425, 59.942853], // busskuret ved rutestart
    zoom: 22, // svært tett på busskur-modellen
    pitch: 60,
    bearing: -45, // samme sørøst-orientering som overview
    duration: 1800,
    endPitch: 80, // slutt-dykk: helt lavt ned, slakt (nesten vannrett) blikk
  },
  akerBrygge: {
    center: [10.728272, 59.909827], // klokketårnet på Aker Brygge
    zoom: 22, // svært tett på klokketårn-modellen
    pitch: 60,
    bearing: -45, // samme sørøst-orientering (snur ikke mot vest)
    duration: 1800,
    endPitch: 80, // slutt-dykk: helt lavt ned, slakt (nesten vannrett) blikk
  },
};

/**
 * Standard startkamera.
 */
export const INITIAL_CAMERA = CAMERA_STATES.overview;

/**
 * Flyr til en navngitt kamerastate med en tydelig BUE:
 * kameraet løftes opp i lufta (zoomer ut), flyr over, og senker
 * seg ned på målet (zoomer inn) – i stedet for å skli lavt over
 * terrenget. Dette gjøres med map.flyTo() + en markert `curve`.
 * @param {import('maplibre-gl').Map} map
 * @param {'overview'|'baerumsVerk'|'akerBrygge'} name
 */
export function goToCamera(map, name) {
  const state = CAMERA_STATES[name];
  if (!state) {
    console.warn(`[camera] ukjent kamerastate: ${name}`);
    return;
  }
  map.flyTo({
    center: state.center,
    zoom: state.zoom,
    pitch: state.pitch,
    bearing: state.bearing,
    // Høyere curve = brattere, mer vertikal bue (opp → over → ned) i stedet
    // for at kameraet skvetter fra side til side lavt over terrenget.
    curve: state.flyCurve ?? 2.2,
    // Raskere flytur.
    speed: state.flySpeed ?? 1.2,
    // Stig godt opp i midten av turen.
    minZoom: state.flyMinZoom ?? 12.5,
    essential: true,
  });

  // Slutt-dykk: når den skrå flyturen er ferdig, tilt kameraet brattere
  // nedover til lav pitch (kun siste del). Kjøres én gang per flytur.
  if (state.endPitch != null) {
    map.once('moveend', () => {
      map.easeTo({
        pitch: state.endPitch,
        duration: 900,
        essential: true,
      });
    });
  }
}

/**
 * Kobler kamera-knappene i DOM til kamerastatene.
 * @param {import('maplibre-gl').Map} map
 */
export function wireCameraControls(map) {
  const buttons = document.querySelectorAll('[data-camera]');
  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.getAttribute('data-camera');
      goToCamera(map, target);
    });
  });
}
